import React, { useEffect, useRef, useState } from 'react';

const DAY_NAMES = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];
const MONTH_NAMES = [
    'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun',
    'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'
];

const pad = (n) => String(n).padStart(2, '0');
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const formatDMY = (isoDate) => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${d}/${m}/${y}`;
};

// Ponedeljak kao prvi dan nedelje u mreži (JS Date.getDay() vraća 0=nedelja).
const buildMonthGrid = (year, month) => {
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
};

// Sopstveni date picker umesto native <input type="date"> - taj prikazuje datum u
// jeziku/formatu browsera (npr. MM/DD/YYYY) i to se ne može pouzdano naterati na dd/mm/yyyy
// preko CSS-a. Poziva onChange sa istim oblikom event objekta ({ target: { value: 'YYYY-MM-DD' } })
// kao native input, tako da svi pozivaoci ostaju nepromenjeni.
const DateInput = ({ value, onChange, style = {}, disabled }) => {
    const [open, setOpen] = useState(false);
    const [viewYear, setViewYear] = useState(() => value ? Number(value.split('-')[0]) : new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState(() => value ? Number(value.split('-')[1]) - 1 : new Date().getMonth());
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const togglePicker = () => {
        if (disabled) return;
        if (value) {
            setViewYear(Number(value.split('-')[0]));
            setViewMonth(Number(value.split('-')[1]) - 1);
        }
        setOpen(o => !o);
    };

    const pickDay = (day) => {
        if (!day) return;
        onChange({ target: { value: toIso(viewYear, viewMonth, day) } });
        setOpen(false);
    };

    const changeMonth = (delta) => {
        let m = viewMonth + delta;
        let y = viewYear;
        if (m < 0) { m = 11; y -= 1; }
        if (m > 11) { m = 0; y += 1; }
        setViewMonth(m);
        setViewYear(y);
    };

    const { flex, width, minWidth } = style;
    const todayIso = new Date().toISOString().split('T')[0];
    const cells = buildMonthGrid(viewYear, viewMonth);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', flex, width, minWidth }}>
            <div
                onClick={togglePicker}
                style={{
                    ...style, cursor: disabled ? 'default' : 'pointer', userSelect: 'none',
                    display: 'flex', alignItems: 'center', boxSizing: 'border-box',
                    backgroundColor: disabled ? '#f1f5f9' : (style.backgroundColor || '#fff'),
                    color: value ? '#1e293b' : '#94a3b8'
                }}
            >
                {value ? formatDMY(value) : 'dd/mm/gggg'}
            </div>
            {open && (
                <div style={styles.popover}>
                    <div style={styles.popoverHeader}>
                        <button type="button" style={styles.navBtn} onClick={() => changeMonth(-1)}>‹</button>
                        <div style={styles.monthLabel}>{MONTH_NAMES[viewMonth]} {viewYear}</div>
                        <button type="button" style={styles.navBtn} onClick={() => changeMonth(1)}>›</button>
                    </div>
                    <div style={styles.weekRow}>
                        {DAY_NAMES.map(d => <div key={d} style={styles.dayName}>{d}</div>)}
                    </div>
                    <div style={styles.grid}>
                        {cells.map((day, i) => {
                            const iso = day ? toIso(viewYear, viewMonth, day) : null;
                            const isSelected = iso === value;
                            const isToday = iso === todayIso;
                            return (
                                <div
                                    key={i}
                                    onClick={() => pickDay(day)}
                                    style={{
                                        ...styles.dayCell,
                                        visibility: day ? 'visible' : 'hidden',
                                        backgroundColor: isSelected ? '#2563eb' : 'transparent',
                                        color: isSelected ? '#fff' : (isToday ? '#2563eb' : '#1e293b'),
                                        fontWeight: isSelected || isToday ? 700 : 400
                                    }}
                                >
                                    {day}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    popover: {
        position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50,
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)', padding: '10px', width: '240px'
    },
    popoverHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
    navBtn: { background: '#f1f5f9', border: 'none', borderRadius: '6px', width: '26px', height: '26px', cursor: 'pointer', fontSize: '14px', color: '#334155' },
    monthLabel: { fontWeight: 600, fontSize: '13px', color: '#1e293b', textTransform: 'capitalize' },
    weekRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' },
    dayName: { textAlign: 'center', fontSize: '11px', color: '#94a3b8', fontWeight: 600 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' },
    dayCell: { textAlign: 'center', padding: '6px 0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }
};

export default DateInput;
